import type { FamilyEnrollmentRow } from './types';

/**
 * The five phases the Enrollments board shows (plus "closed" for declined
 * families). These are a presentation grouping over the existing
 * FamilyEnrollment states — nothing here changes what the backend stores.
 */
export type Phase = 'setup' | 'open' | 'in_review' | 'offered' | 'complete' | 'closed';

export const PHASES: Array<{ key: Phase; label: string; hint: string }> = [
  { key: 'setup', label: 'Setup', hint: 'Build the family, queue the invite' },
  { key: 'open', label: 'Open', hint: 'Family has the link' },
  { key: 'in_review', label: 'In review', hint: 'Decide per student' },
  { key: 'offered', label: 'Offered', hint: 'Packet sent — sign and pay' },
  { key: 'complete', label: 'Complete', hint: 'Signed and paid — enrolled' },
  { key: 'closed', label: 'Declined', hint: 'Not enrolling this year' },
];

/**
 * Once the school enrolls with the checklist, a year is placed by its access
 * alone, which never puts one in review or offered.
 */
const CHECKLIST_PHASES: typeof PHASES = [
  { key: 'setup', label: 'Setup', hint: "Invite when you're ready" },
  { key: 'open', label: 'Open', hint: 'Working through the checklist' },
  { key: 'complete', label: 'Complete', hint: 'Every step done' },
  { key: 'closed', label: 'Declined', hint: 'Not enrolling this year' },
];

/** The board's columns, and the drawer's steps, for this school. */
export function phasesFor(switchOn: boolean): typeof PHASES {
  return switchOn ? CHECKLIST_PHASES : PHASES;
}

const PHASE_BY_STATE: Record<string, Phase> = {
  DRAFT: 'setup',
  READY_TO_INVITE: 'setup',
  INVITED: 'open',
  IN_PROGRESS: 'open',
  NEEDS_REVIEW: 'in_review',
  APPROVED: 'offered',
  PACKET_SENT: 'offered',
  PACKET_VIEWED: 'offered',
  PACKET_SUBMITTED: 'offered',
  PACKET_COMPLETED: 'complete',
  DECLINED: 'closed',
};

const PHASE_BY_ACCESS: Record<string, Phase> = {
  NOT_INVITED: 'setup',
  ENROLLING: 'open',
  ENROLLED: 'complete',
  DECLINED: 'closed',
};

/** What the office calls a new-style year's access. */
export const ACCESS_LABELS: Record<string, string> = {
  NOT_INVITED: 'Not invited',
  ENROLLING: 'Enrolling',
  ENROLLED: 'Enrolled',
  DECLINED: 'Declined',
};

/**
 * A year opened by the checklist invite ("set") is run by its `access`, not by
 * the old flow's `state`. Once the school enrolls with the checklist
 * (`switchOn`), every year is: `access` is kept current for old-flow years too.
 * Without the switch, old-flow rows ("legacy") keep reading `state`, and that
 * is still the question the old flow's own actions ask of a row.
 */
export function isNewStyle(row: { accessSource?: string | null }, switchOn = false): boolean {
  return switchOn || row.accessSource === 'SET';
}

export function phaseFor(
  row: { state: string; access?: string | null; accessSource?: string | null },
  switchOn = false
): Phase {
  if (isNewStyle(row, switchOn)) return PHASE_BY_ACCESS[row.access ?? ''] ?? 'setup';
  return PHASE_BY_STATE[row.state] ?? 'setup';
}

export function isPhase(value: string | null | undefined): value is Phase {
  return PHASES.some((phase) => phase.key === value);
}

export type Tone = 'muted' | 'neutral' | 'warning' | 'danger' | 'success';

export interface EnrollmentStatus {
  text: string;
  tone: Tone;
}

/** GraphQL date scalars arrive as `unknown`; keep only real strings. */
export function iso(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

export function formatShortDate(value: unknown): string {
  const raw = iso(value);
  if (!raw) return '';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function formatMoney(cents: number): string {
  const dollars = cents / 100;
  return dollars.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

type InvoiceRow = FamilyEnrollmentRow['invoices'][number];

const OPEN_STATUSES = new Set(['UNPAID', 'PAYMENT_FAILED']);

export function isOverdue(invoice: InvoiceRow, today: Date = new Date()): boolean {
  const due = iso(invoice.dueDate);
  if (!due || !OPEN_STATUSES.has(invoice.status)) return false;
  return new Date(`${due}T23:59:59`) < today;
}

export interface InvoiceSummary {
  count: number;
  totalCents: number;
  paidCents: number;
  openCents: number;
  overdueCents: number;
  overdueCount: number;
  draftCount: number;
}

export function summarizeInvoices(invoices: InvoiceRow[], today: Date = new Date()): InvoiceSummary {
  return invoices.reduce<InvoiceSummary>(
    (summary, invoice) => {
      summary.count += 1;
      summary.totalCents += invoice.amountCents;
      if (invoice.status === 'PAID') summary.paidCents += invoice.amountCents;
      if (OPEN_STATUSES.has(invoice.status) || invoice.status === 'PAYMENT_PROCESSING') summary.openCents += invoice.amountCents;
      if (invoice.status === 'DRAFT') summary.draftCount += 1;
      if (isOverdue(invoice, today)) {
        summary.overdueCents += invoice.amountCents;
        summary.overdueCount += 1;
      }
      return summary;
    },
    { count: 0, totalCents: 0, paidCents: 0, openCents: 0, overdueCents: 0, overdueCount: 0, draftCount: 0 }
  );
}

export const PAYMENT_SCHEDULE_LABELS: Record<string, string> = {
  monthly: 'Monthly',
  biannual: 'Two payments',
  annual: 'Paid in full',
};

interface Windows {
  reenrollmentOpen?: boolean | null;
  openEnrollmentOpen?: boolean | null;
}

/** The one-line status a board card and the drawer header show for an enrollment. */
export function describeEnrollment(
  row: FamilyEnrollmentRow,
  windows: Windows = {},
  today: Date = new Date(),
  switchOn = false
): EnrollmentStatus {
  if (isNewStyle(row, switchOn)) {
    const tones: Record<string, Tone> = { NOT_INVITED: 'muted', ENROLLING: 'neutral', ENROLLED: 'success', DECLINED: 'muted' };
    return { text: ACCESS_LABELS[row.access] ?? row.access, tone: tones[row.access] ?? 'muted' };
  }

  const windowOpen = row.enrollmentKind === 'RE_ENROLLMENT' ? windows.reenrollmentOpen : windows.openEnrollmentOpen;

  switch (row.state) {
    case 'DRAFT':
      return { text: row.canQueueForInvite ? 'Draft · ready to queue' : 'Draft · needs a contact email and a student', tone: 'muted' };
    case 'READY_TO_INVITE':
      return windowOpen
        ? { text: `Queued ${formatShortDate(row.queuedAt)} · window open`, tone: 'neutral' }
        : { text: `Queued ${formatShortDate(row.queuedAt)} · window closed`, tone: 'warning' };
    case 'INVITED':
      return { text: `Invited ${formatShortDate(row.sentAt)} · not opened`, tone: 'muted' };
    case 'IN_PROGRESS':
      return { text: row.enrollmentKind === 'RE_ENROLLMENT' ? 'Re-enrollment form started' : 'Application started', tone: 'neutral' };
    case 'NEEDS_REVIEW':
      return { text: `Submitted ${formatShortDate(row.submittedAt)}`, tone: 'warning' };
    case 'APPROVED':
      return { text: `Approved ${formatShortDate(row.approvedAt)} · packet not sent`, tone: 'neutral' };
    case 'PACKET_SENT':
      return { text: `Packet sent ${formatShortDate(row.packetSentAt)} · not opened`, tone: 'muted' };
    case 'PACKET_VIEWED':
      return { text: `Packet opened ${formatShortDate(row.packetViewedAt)} · unsigned`, tone: 'neutral' };
    case 'PACKET_SUBMITTED':
      return { text: `Signed ${formatShortDate(row.packetSubmittedAt)} · payment processing`, tone: 'neutral' };
    case 'PACKET_COMPLETED': {
      const invoices = summarizeInvoices(row.invoices ?? [], today);
      if (invoices.overdueCount > 0) {
        return { text: `${formatMoney(invoices.overdueCents)} overdue`, tone: 'danger' };
      }
      if (invoices.count === 0) return { text: 'Enrolled', tone: 'success' };
      if (invoices.openCents === 0 && invoices.draftCount === 0) return { text: 'Enrolled · paid in full', tone: 'success' };
      const schedule = PAYMENT_SCHEDULE_LABELS[row.paymentScheduleType ?? ''];
      return { text: `Enrolled · ${schedule ? `${schedule.toLowerCase()}, ` : ''}current`, tone: 'success' };
    }
    case 'DECLINED':
      return { text: 'Declined', tone: 'muted' };
    default:
      return { text: row.state, tone: 'muted' };
  }
}

export function studentsLine(row: FamilyEnrollmentRow): string {
  const active = (row.studentEnrollments ?? []).filter((se) => !['WITHDRAWN', 'DENIED'].includes(se.state));
  if (active.length === 0) return 'No students yet';
  return active.map((se) => [se.person.firstName, se.gradeLevel].filter(Boolean).join(' · ')).join(', ');
}

/** Timeline entries for the drawer, newest first. */
export function activityFor(row: FamilyEnrollmentRow): Array<{ label: string; at: string }> {
  const entries: Array<[string, unknown]> = [
    ['Enrollment created', row.createdAt],
    ['Queued for invite', row.queuedAt],
    [row.enrollmentKind === 'RE_ENROLLMENT' ? 'Re-enrollment invite sent' : 'Application link sent', row.sentAt],
    ['Form submitted', row.submittedAt],
    ['Review completed', row.approvedAt],
    ['Packet sent', row.packetSentAt],
    ['Packet opened', row.packetViewedAt],
    ['Packet signed', row.packetSubmittedAt],
  ];
  return entries
    .flatMap(([label, value]) => {
      const at = iso(value);
      return at ? [{ label, at }] : [];
    })
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
}
